import { useEffect, useRef, useState } from "react";
import { useCurrentUser } from "../lib/userInfo.js";
import { useMyPermissions, roleNames } from "../lib/permissions.js";
import { signedInRole } from "../lib/userRoles.js";
import { AUTH_REQUIRED } from "../lib/featureFlags.js";
import LogoutDialog from "./LogoutDialog.jsx";

// The head of the profile menu: who is signed in and their role, in words.
// With the sidebar open its row already shows the email, so the menu shows
// only the role (styles.css hides the email there).
function Identity({ user, caps }) {
  const role = signedInRole(roleNames(caps), user?.currentTenant?.role);
  return (
    <div className="grid gap-[2px] leading-[1.35]">
      {user?.email ? (
        <div className="break-all text-[13px] font-medium text-[color:var(--ink)]" data-account-identity="">{user.email}</div>
      ) : null}
      {role ? <div className="text-[12.5px] text-[color:var(--muted)]" data-account-role="">{role}</div> : null}
    </div>
  );
}

// The account at the sidebar's foot (AppNav.jsx): who is signed in, in which
// organization, behind one row whose menu holds the rest. Folded, the column
// shows only its initial, and the menu opens beside it.
export default function SidebarFooter({
  theme,
  onToggleTheme,
  onOpenAccount,
  onSendFeedback,
}) {
  const user = useCurrentUser();
  const caps = useMyPermissions();
  const orgName = user?.currentTenant?.tenantName || "—";
  const initial = (user?.email || orgName).charAt(0).toUpperCase();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    userMenuRef.current?.querySelector('[role="menuitem"]')?.focus();
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "pointerdown" && userMenuRef.current?.contains(event.target)) return;
      setUserMenuOpen(false);
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        profileRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [userMenuOpen]);

  const item = "cursor-pointer rounded-[6px] px-[8px] py-[7px] text-left text-[13px] text-[color:var(--muted)] hover:bg-[color:var(--side-raise)] hover:text-[color:var(--ink)] focus-visible:bg-[color:var(--side-raise)] focus-visible:text-[color:var(--ink)] focus-visible:outline-none";

  return (
    <div ref={userMenuRef} data-account-foot="" className="relative">
      <button
        type="button"
        ref={profileRef}
        data-account-trigger=""
        className="grid h-[40px] w-full min-w-0 cursor-pointer grid-cols-[28px_minmax(0,1fr)] items-center gap-x-[10px] overflow-hidden rounded-[8px] border-0 bg-transparent px-[4px] text-left hover:bg-[color-mix(in_srgb,var(--side-raise)_55%,transparent)]"
        aria-haspopup="menu"
        aria-expanded={userMenuOpen}
        onClick={() => setUserMenuOpen((open) => !open)}
      >
        <span className="grid h-[28px] w-[28px] place-items-center rounded-full bg-[color:var(--side-raise)] text-[12px] font-semibold text-[color:var(--ink)]" aria-hidden="true">{initial}</span>
        <span className="grid min-w-0 whitespace-nowrap">
          <span className="truncate text-[13px] font-medium leading-[18px] text-[color:var(--ink)]">{user?.email || orgName}</span>
          {user?.email ? <span className="truncate text-[11px] leading-[15px] text-[color:var(--muted-soft)]">{orgName}</span> : null}
        </span>
      </button>
      {userMenuOpen ? (
        <div
          role="menu"
          aria-label="Profile"
          onKeyDown={(event) => {
            const items = [...event.currentTarget.querySelectorAll('[role="menuitem"]')];
            const index = items.indexOf(document.activeElement);
            const next = event.key === "ArrowDown" ? (index + 1) % items.length
              : event.key === "ArrowUp" ? (index - 1 + items.length) % items.length
                : event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : null;
            if (next !== null) { event.preventDefault(); items[next]?.focus(); }
            if (event.key === "Tab") { setUserMenuOpen(false); profileRef.current?.focus(); }
          }}
          className="absolute bottom-[calc(100%+8px)] left-0 z-50 grid w-[232px] gap-[2px] rounded-[10px] border border-[color:var(--line-strong)] bg-[color:var(--panel)] p-[6px] shadow-lg"
        >
          <div className="-mx-[6px] -mt-[6px] mb-[6px] rounded-t-[9px] border-b border-[color:var(--line-strong)] bg-[color-mix(in_srgb,var(--ink)_7%,transparent)] px-[14px] py-[11px]" data-account-head="">
            <Identity user={user} caps={caps} />
          </div>
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => { setUserMenuOpen(false); onOpenAccount?.(); }}
            data-drawer-pick=""
          >
            User settings
          </button>
          <button
            type="button"
            role="menuitem"
            className={item}
            onClick={() => { setUserMenuOpen(false); onSendFeedback?.(); }}
            data-drawer-pick=""
          >
            Send feedback or report a bug
          </button>
          {onToggleTheme ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() => { setUserMenuOpen(false); onToggleTheme(); }}
            >
              {theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            </button>
          ) : null}
          {AUTH_REQUIRED ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() => { setUserMenuOpen(false); setLogoutOpen(true); }}
            >
              Log out
            </button>
          ) : null}
        </div>
      ) : null}
      {AUTH_REQUIRED ? (
        <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
      ) : null}
    </div>
  );
}
